import { searchCartoonAction } from "../../store/cartoons/search/SearchCartoonSlice";
import getTranscribeData from "../../store/aiAgent/transcribe/Transcribe";
import searchCartoon from "../../store/cartoons/search/SearchCartoon";
import { productsAction } from "../../store/products/ProductsSlice";
import { getCategoryIds } from "../../utils/helpers/aiAgent/utils";
import fetchProducts from "../../store/products/FetchProducts";
import getPromptData from "../../store/aiAgent/prompt/Prompt";
import { ROUTES } from "../../utils/helpers/aiAgent/routes";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useEffect, useRef } from "react";
import Cookies from "js-cookie";

const VOICE_PLACEHOLDER = "voice_message_processing";

const useAgentMessages = ({ setChatMessages, setLoading, setOpen, allCategories }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = Cookies.get("auth_token");
  const transcribeAbortRef = useRef(null);
  const promptAbortRef = useRef(null);
  const cancelledRef = useRef(false);
  const hasNavigatedRef = useRef(false);
  const booksDoneRef = useRef(false);
  const cartoonsDoneRef = useRef(false);
  const promptData = useSelector((state) => state.promptData);
  const transcribeData = useSelector((state) => state.transcribeData);
  const searchProductsData = useSelector((state) => state.products);
  const searchCartoonData = useSelector((state) => state.searchCartoon);
  const preferedLanguage = Cookies.get("preferedLanguage");
  
  const pushError = (text) =>
    setChatMessages((prev) => [
      ...prev.filter((m) => m.text !== VOICE_PLACEHOLDER),
      { id: Date.now(), text, sender: "user", type: "error" },
    ]);

  const replaceLoading = (payload) =>
    setChatMessages((prev) =>
      prev.map((m) => m.loading ? { ...m, text: payload?.key, resetsAt: payload?.resetsAt, loading: false, type: "error" } : m)
    );

  function sendVoice(wavBlob) {
    cancelledRef.current = false;
    transcribeAbortRef.current = new AbortController();
    dispatch(getTranscribeData(wavBlob, token, transcribeAbortRef.current.signal));
    setChatMessages((prev) => [
      ...prev,
      { id: Date.now(), text: VOICE_PLACEHOLDER, sender: "user", loading: true },
    ]);
    setLoading(true);
  };

  function sendText(text) {
    const finalMessage = String(text || "").trim();
    if (!finalMessage) return;
    cancelledRef.current = false;
    hasNavigatedRef.current = false;
    booksDoneRef.current = false;
    cartoonsDoneRef.current = false;

    promptAbortRef.current = new AbortController();
    setChatMessages((prev) => [
      ...prev.filter((m) => m.text !== VOICE_PLACEHOLDER),
      { id: Date.now(), text: finalMessage, sender: "user" },
      { id: Date.now() + 1, text: "request_in_progress", sender: "ai", loading: true },
    ]);
    setLoading(true);
    dispatch(getPromptData(finalMessage, token, promptAbortRef.current.signal));
  };

  function cancel() {
    cancelledRef.current = true;
    transcribeAbortRef.current?.abort();
    promptAbortRef.current?.abort();
    setLoading(false);
    replaceLoading({ key: "request_cancelled" });
  };

  const handleNavigation = (route) => {
    setOpen("");
    setChatMessages([]);
    hasNavigatedRef.current = true;
    dispatch(productsAction.clearProducts());
    dispatch(searchCartoonAction.clearSearchCartoons());

    const { searchText, categories = [], ageGroup = [], type } = promptData.prompt?.searchParameters;
    const ids = getCategoryIds(allCategories, categories);
    const storageMap = {
      products: { key: "checkedData", searchKey: "productsSearchText", data: { categories: ids, ageGroup, type } },
      cartoons: { key: "cartoonCheckedData", searchKey: "cartoonsSearchText", data: { categories: ids, ageGroup } },
    };

    const config = storageMap[route];
    if (config) {
      sessionStorage.setItem(config.key, JSON.stringify(config.data));
      sessionStorage.setItem(config.searchKey, searchText);
    }

    navigate(`/${route}`);
  };

  useEffect(() => {
    if (Object.keys(searchProductsData?.products?.products ?? {})?.length > 0 || searchProductsData?.error) {
      booksDoneRef.current = true;
      handleSearchResults();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchProductsData]);

  useEffect(() => {
    const hasData = searchCartoonData?.data && Object.keys(searchCartoonData?.data ?? {})?.length > 0;
    if (hasData || searchCartoonData?.error) {
      cartoonsDoneRef.current = true;
      handleSearchResults();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchCartoonData]);

  const handleSearchResults = () => {
    if (!booksDoneRef.current || !cartoonsDoneRef.current) return;
    if (hasNavigatedRef.current) return;

    if (searchProductsData?.error || searchCartoonData?.error) {
      replaceLoading({ key: "something_went_wrong" });
      return
    }

    const books = searchProductsData?.products?.products?.products ?? [];
    const cartoons = searchCartoonData?.data?.data?.cartoons ?? [];
    const hasBooks = !!books.length;
    const hasCartoons = !!cartoons.length;

    if (!hasBooks && !hasCartoons) return replaceLoading({ key: "search_no_suitable_books_or_cartoons" });
    if (hasBooks && !hasCartoons) return handleNavigation("products")
    if (!hasBooks && hasCartoons) return handleNavigation("cartoons");
    if (hasBooks && hasCartoons) {
      const searchParams = promptData.prompt?.searchParameters
      setChatMessages((prev) => prev.map((m) => m.loading ? { ...m, loading: false, type: "choice", payload: searchParams } : m));
    }
  };

  useEffect(() => {
    if (!transcribeData?.transcribe?.text && !transcribeData?.error?.error) return;
    if (cancelledRef.current) return;

    if (transcribeData.transcribe?.text) {
      sendText(transcribeData.transcribe.text);
    } else {
      pushError("something_went_wrong");
      setLoading(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transcribeData]);

  const handleSearch = ({ searchText, type, ageGroup, categories }) => {
    const finalType = Array.isArray(type) ? type.length > 1 ? "both" : type[0] || "" : type || "";
    const ids = getCategoryIds(allCategories, categories);
    const filters = {
      type: finalType,
      ageGroup: ageGroup?.length ? ageGroup : undefined,
      categories: ids?.length ? ids : undefined,
    };

    dispatch(fetchProducts(filters, 1, searchText, "*", "", preferedLanguage));
    dispatch(searchCartoon({ searchText, ageGroup, categories: ids }, undefined, 1));
  };

  useEffect(() => {
    if (!promptData?.prompt && !promptData?.error) return;
    if (cancelledRef.current) return;
    setLoading(false);

    const { action, destination, searchParameters } = promptData.prompt || {};
    if (action === "navigate") {
      const routePath = ROUTES?.[destination];
      if (!routePath) return replaceLoading({ key: "page_not_found" });

      navigate(routePath);
      setOpen("");
      setChatMessages([]);
    } else if (action === "search") {
      handleSearch(searchParameters);
    }

    if (promptData.error) {
      const errorKey = promptData.error?.Error || "something_went_wrong";

      replaceLoading({
        key: errorKey,
        resetsAt: errorKey === "ai_limit_reached" ? promptData.error?.resetsAt : undefined
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promptData]);

  return { sendVoice, sendText, cancel };
};

export default useAgentMessages;